<script setup>
/* eslint semi: 1 */

import { onMounted, inject } from 'vue';
import { updateUserAttributes as AmplifyUpdateUserAttributes } from 'aws-amplify/auth';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';

import EventBus from '@/components/mixins/event-bus-mixin';
import FormInput from '@/components/common/form/form-input-tmpl.vue';
import FormButton from '@/components/common/form/form-button-tmpl.vue';

import { t } from '@/i18n/i18ns';

const $router = inject('$routerInstance');
const $api = inject('$api');
const $utils = inject('$utils');

defineProps({
  isSignInMini: {
    type: Boolean,
    default: false
  },
  cognitoUser: {
    type: Object,
    required: true
  },
});

const emit = defineEmits(['successSubmit']);

const schema = yup.object({
  firstName: yup.string()
    .required('label.requiredFirstName'),
  lastName: yup.string()
    .required('label.requiredLastName'),
});

const {
  errors: formErrors,
  handleSubmit,
  defineField,
  isSubmitting,
} = useForm({
  validationSchema: toTypedSchema(schema)
});

const [firstName] = defineField('firstName');
const [lastName] = defineField('lastName');

const performUpdateAttributes = async (values) => {
  try {
    await AmplifyUpdateUserAttributes({
      userAttributes: {
        given_name: values.firstName,
        family_name: values.lastName,
      }
    });
  } catch (err) {
    return { error: err };
  }
  return { result: 'success' };
};

const onSubmit = handleSubmit(async (values) => {
  const { error, result } = await performUpdateAttributes(values);
  if (error) {
    console.error('Error updating user attributes:', error);

    EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
      type: 'error',
      alert: {
        message: t('label.loginError')
      },
      callbacks: { eventOnClose: () => { $router.push('/', () => {}); } }
    });
    return;
  }

  console.log('User attributes updated successfully:', result);
  emit('successSubmit', { success: true });
});

onMounted(async () => {
  $utils.mainLoader(true);
  const response = await $api.get(`${CONFIG.API_BASE_URL}/group/users/me`);
  const userDetails = response.data;
  firstName.value = userDetails.firstName || '';
  lastName.value = userDetails.lastName || '';
  $utils.mainLoader(false);
});
</script>

<template>
  <form
    class="max-[1919px]:my-3 min-[1920px]:my-5 flex flex-col flex-1 w-full justify-between"
    data-component-element-id="sign-in-update-details-form"
    @submit="onSubmit"
  >
    <div />
    <!-- Form -->
    <div class="flex flex-col max-[1919px]:gap-4 min-[1920px]:gap-6 max-[1919px]:mb-8 min-[1920px]:mb-10">
      <form-input
        v-model="firstName"
        id="first-name"
        data-component-element-id="sign-in-update-details-first-name-input"
        type="text"
        inputTheme="light"
        :size="isSignInMini ? 'xs' : 'md'"
        required
        :disabled="isSubmitting"
        :label="$t('label.firstName')"
        :validationStatus="formErrors?.firstName ? 'error' : undefined"
      >
        <template
          v-if="formErrors?.firstName"
          #validationTransparent
        >
          {{ $t(formErrors.firstName) }}
        </template>
      </form-input>

      <form-input
        v-model="lastName"
        id="last-name"
        data-component-element-id="sign-in-update-details-last-name-input"
        type="text"
        inputTheme="light"
        :size="isSignInMini ? 'xs' : 'md'"
        required
        :disabled="isSubmitting"
        :label="$t('label.lastName')"
        :validationStatus="formErrors?.lastName ? 'error' : undefined"
      >
        <template
          v-if="formErrors?.lastName"
          #validationTransparent
        >
          {{ $t(formErrors.lastName) }}
        </template>
      </form-input>
    </div>

    <form-button
      type="submit"
      buttonTheme="light"
      :size="isSignInMini ? 'xs' : 'md'"
      data-component-element-id="sign-in-update-details-submit-btn"
      :loading="isSubmitting"
      :disabled="isSubmitting"
      :label="$t('label.confirm')"
      @click="onSubmit"
    />
  </form>
</template>
